import React, { useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform,
    Pressable,
    StyleSheet,
    Text,
    View,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import { DivineBackground } from "../../shared/ui/DivineBackground";
import { Header } from "../../shared/ui/Header";
import { Button, Input } from "../../shared/ui/FormElements";

import { Flash } from "../../shared/utils/Flash";
import { ForgotMPINService } from "../../services/ForgotMPINService";
import type { AuthStackParamList } from "../../navigation/navigationTypes";
import type {
    ForgotPasswordRequestModel,
    OtpChannelInfoModel,
    OtpVerificationModel,
    VerificationParameterModel,
} from "../../models/OtpVerificationModel";
import type { UpdatePasswordModel } from "../../models/UpdatePasswordModel";
import {
    OTP_MAX_LENGTH,
    OTP_PLACEHOLDER,
    getMaskedOtpLabel,
    hasAnyVerificationChannel,
    hasOtpValue,
    normalizeOtpChannelInfo,
    normalizeVerificationParameter,
} from "../../shared/utils/verification";

type Step = "identifier" | "otp" | "reset";
type Nav = NativeStackNavigationProp<AuthStackParamList, "ForgotMPIN">;

const RESEND_OTP_SECONDS = 5 * 60;
const EMPTY_CONFIG: VerificationParameterModel = { email: false, mobile: false };
const EMPTY_OTP_CHANNELS: OtpChannelInfoModel = {
    email: false,
    mobile: false,
    maskedEmail: null,
    maskedMobileNumber: null,
};

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export function ForgotMPINScreen() {
    const navigation = useNavigation<Nav>();

    const [step, setStep] = useState<Step>("identifier");
    const [loading, setLoading] = useState(false);
    const [configLoading, setConfigLoading] = useState(true);

    const [verificationConfig, setVerificationConfig] = useState<VerificationParameterModel>(EMPTY_CONFIG);
    const [otpChannels, setOtpChannels] = useState<OtpChannelInfoModel>(EMPTY_OTP_CHANNELS);
    const [temporaryToken, setTemporaryToken] = useState("");

    const [mobile, setMobile] = useState("");
    const [email, setEmail] = useState("");
    const [mobileOtp, setMobileOtp] = useState("");
    const [emailOtp, setEmailOtp] = useState("");
    const [resendTimer, setResendTimer] = useState(0);

    const [newMpin, setNewMpin] = useState("");
    const [confirmMpin, setConfirmMpin] = useState("");
    const [userId, setUserId] = useState("");

    const trimmedMobile = useMemo(() => mobile.trim(), [mobile]);
    const trimmedEmail = useMemo(() => email.trim(), [email]);
    const trimmedMobileOtp = useMemo(() => mobileOtp.trim(), [mobileOtp]);
    const trimmedEmailOtp = useMemo(() => emailOtp.trim(), [emailOtp]);

    const hasValidMobile = useMemo(() => trimmedMobile.length === 10, [trimmedMobile]);
    const hasValidEmail = useMemo(
        () => trimmedEmail.length > 0 && isValidEmail(trimmedEmail),
        [trimmedEmail]
    );
    const hasValidMobileOtp = useMemo(() => hasOtpValue(trimmedMobileOtp), [trimmedMobileOtp]);
    const hasValidEmailOtp = useMemo(() => hasOtpValue(trimmedEmailOtp), [trimmedEmailOtp]);
    const isMpinValid = useMemo(
        () => newMpin.length === 6 && newMpin === confirmMpin,
        [newMpin, confirmMpin]
    );
    const resendTimeLabel = useMemo(() => {
        const minutes = Math.floor(resendTimer / 60);
        const seconds = resendTimer % 60;
        return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }, [resendTimer]);
    const canSendOtp = useMemo(() => {
        if (configLoading || loading || !hasAnyVerificationChannel(verificationConfig)) {
            return false;
        }

        const hasIdentifier =
            (verificationConfig.mobile && hasValidMobile) ||
            (verificationConfig.email && hasValidEmail);

        return hasIdentifier;
    }, [configLoading, hasValidEmail, hasValidMobile, loading, verificationConfig]);
    const canVerifyOtp = useMemo(() => {
        const readyChannels =
            Number(otpChannels.mobile && hasValidMobileOtp) +
            Number(otpChannels.email && hasValidEmailOtp);

        return readyChannels === 1;
    }, [hasValidEmailOtp, hasValidMobileOtp, otpChannels]);
    const recoveryHint = useMemo(() => {
        if (configLoading) {
            return "Loading verification options...";
        }

        if (!hasAnyVerificationChannel(verificationConfig)) {
            return "Password recovery is not available right now. Please contact support.";
        }

        if (verificationConfig.mobile && verificationConfig.email) {
            return "Enter your registered mobile number, email address, or both.";
        }

        if (verificationConfig.mobile) {
            return "Enter your registered mobile number.";
        }

        return "Enter your registered email address.";
    }, [configLoading, verificationConfig]);
    const mobileOtpLabel = useMemo(
        () => getMaskedOtpLabel("Mobile OTP", otpChannels.maskedMobileNumber),
        [otpChannels.maskedMobileNumber]
    );
    const emailOtpLabel = useMemo(
        () => getMaskedOtpLabel("Email OTP", otpChannels.maskedEmail),
        [otpChannels.maskedEmail]
    );

    useEffect(() => {
        let mounted = true;

        const loadConfig = async () => {
            setConfigLoading(true);
            try {
                const res = await ForgotMPINService.getForgotPasswordConfig();

                if (res?.status !== 200) {
                    Flash.error(res?.message ?? "Failed to load recovery settings");
                    return;
                }

                if (!mounted) return;

                setVerificationConfig(normalizeVerificationParameter(res?.data));
            } catch (e: any) {
                Flash.error(e?.message ?? "Failed to load recovery settings");
            } finally {
                if (mounted) {
                    setConfigLoading(false);
                }
            }
        };

        loadConfig();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (resendTimer <= 0) return;

        const timer = setInterval(() => {
            setResendTimer((current) => (current > 0 ? current - 1 : 0));
        }, 1000);

        return () => clearInterval(timer);
    }, [resendTimer]);

    const buildForgotPasswordPayload = (): ForgotPasswordRequestModel | null => {
        if (!hasAnyVerificationChannel(verificationConfig)) {
            Flash.error("Password recovery is not available right now.");
            return null;
        }

        if (verificationConfig.mobile && trimmedMobile && !hasValidMobile) {
            Flash.error("Please enter a valid 10 digit mobile number");
            return null;
        }

        if (verificationConfig.email && trimmedEmail && !hasValidEmail) {
            Flash.error("Please enter a valid email address");
            return null;
        }

        const payload: ForgotPasswordRequestModel = {};

        if (verificationConfig.mobile && hasValidMobile) {
            payload.mobileNumber = trimmedMobile;
        }

        if (verificationConfig.email && hasValidEmail) {
            payload.email = trimmedEmail;
        }

        if (!payload.mobileNumber && !payload.email) {
            if (verificationConfig.mobile && verificationConfig.email) {
                Flash.error("Enter your registered mobile number, email address, or both.");
            } else if (verificationConfig.mobile) {
                Flash.error("Enter your registered mobile number.");
            } else {
                Flash.error("Enter your registered email address.");
            }

            return null;
        }

        return payload;
    };

    const sendOtp = async () => {
        const payload = buildForgotPasswordPayload();

        if (!payload) return;

        setLoading(true);
        try {
            const res = await ForgotMPINService.sendOtp(payload);
            const channelInfo = normalizeOtpChannelInfo(res?.data);

            if (res?.status !== 200) {
                Flash.error(res?.message ?? "Failed to send OTP");
                return;
            }

            if (!res?.token) {
                Flash.error("Verification session could not be created. Please try again.");
                return;
            }

            if (!hasAnyVerificationChannel(channelInfo)) {
                Flash.error("No OTP channel is available for this request.");
                return;
            }

            Flash.success("Success!", "OTP Sent");
            setTemporaryToken(res.token);
            setOtpChannels(channelInfo);
            setMobileOtp("");
            setEmailOtp("");
            setResendTimer(RESEND_OTP_SECONDS);
            setStep("otp");
        } catch (e: any) {
            Flash.error(e?.message ?? "Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    const verifyOtp = async () => {
        if (!temporaryToken) {
            return Flash.error("Verification session expired. Please request a new OTP.");
        }

        if (otpChannels.mobile && trimmedMobileOtp && !hasValidMobileOtp) {
            return Flash.error("Enter the complete mobile OTP.");
        }

        if (otpChannels.email && trimmedEmailOtp && !hasValidEmailOtp) {
            return Flash.error("Enter the complete email OTP.");
        }

        const requests: OtpVerificationModel[] = [];

        if (otpChannels.mobile && hasValidMobileOtp) {
            requests.push({ otp: trimmedMobileOtp, code: "Mobile" });
        }

        if (otpChannels.email && hasValidEmailOtp) {
            requests.push({ otp: trimmedEmailOtp, code: "Email" });
        }

        if (requests.length === 0) {
            if (otpChannels.mobile && otpChannels.email) {
                return Flash.error("Enter either the mobile OTP or the email OTP.");
            }

            if (otpChannels.mobile) {
                return Flash.error("Enter the mobile OTP.");
            }

            return Flash.error("Enter the email OTP.");
        }

        if (requests.length > 1) {
            return Flash.error("Verify one OTP at a time to continue.");
        }

        setLoading(true);
        try {
            const res = await ForgotMPINService.verifyOtp(requests[0], temporaryToken);

            if (res?.status !== 200) {
                Flash.error(res?.message ?? "OTP verification failed");
                return;
            }

            const id = String(res?.data ?? "").trim();

            if (!id) {
                Flash.error("UserId not found in OTP verify response");
                return;
            }

            setUserId(id);
            Flash.success("Success!", "OTP Verified");
            setStep("reset");
        } catch (e: any) {
            Flash.error(e?.message ?? "OTP verification error");
        } finally {
            setLoading(false);
        }
    };

    const resetMpin = async () => {
        if (!userId) return Flash.error("UserId missing. Please verify OTP again.");
        if (newMpin.length !== 6) return Flash.error("MPIN must be 6 digits");
        if (newMpin !== confirmMpin) return Flash.error("MPIN and Confirm MPIN must match");

        setLoading(true);
        try {
            const payload: UpdatePasswordModel = {
                userId: userId,
                password: newMpin,
            };

            const res = await ForgotMPINService.updatePassword(payload);

            if (res?.status !== 200) {
                Flash.error(res?.message ?? "Failed to reset MPIN");
                return;
            }

            Flash.success("Success!", "MPIN Updated");
            navigation.replace("Login");
        } catch (e: any) {
            Flash.error(e?.message ?? "Failed to reset MPIN");
        } finally {
            setLoading(false);
        }
    };

    const backToIdentifier = () => {
        setStep("identifier");
        setTemporaryToken("");
        setOtpChannels(EMPTY_OTP_CHANNELS);
        setMobileOtp("");
        setEmailOtp("");
        setResendTimer(0);
        setNewMpin("");
        setConfirmMpin("");
        setUserId("");
    };

    const backToOtp = () => {
        setStep("otp");
        setNewMpin("");
        setConfirmMpin("");
    };

    return (
        <DivineBackground overlayColor="#FFF8E7" overlayOpacity={0.88}>
            <Header title="Forgot MPIN" showBack />

            <KeyboardAvoidingView
                style={{ flex: 1 }}
                behavior={Platform.OS === "ios" ? "padding" : "height"}
                keyboardVerticalOffset={Platform.OS === "ios" ? 18 : 24}
            >
                <View style={styles.center}>
                    <View style={styles.cardSmall}>
                        {step === "identifier" ? (
                            <View style={{ gap: 14 }}>
                                <Text style={styles.h2}>Reset Access</Text>
                                <Text style={styles.p}>{recoveryHint}</Text>

                                {configLoading ? (
                                    <View style={styles.loadingWrap}>
                                        <ActivityIndicator size="small" color="#F97316" />
                                    </View>
                                ) : null}

                                {verificationConfig.mobile ? (
                                    <Input
                                        label="Mobile Number"
                                        placeholder="Enter registered mobile"
                                        keyboardType="number-pad"
                                        maxLength={10}
                                        value={mobile}
                                        onChangeText={(t) => setMobile(t.replace(/\D/g, ""))}
                                    />
                                ) : null}

                                {verificationConfig.email ? (
                                    <Input
                                        label="Email Address"
                                        placeholder="Enter registered email"
                                        keyboardType="email-address"
                                        autoCapitalize="none"
                                        value={email}
                                        onChangeText={setEmail}
                                    />
                                ) : null}

                                <View style={styles.fullWidth}>
                                    <Button
                                        title={loading ? "Sending..." : "Send OTP"}
                                        onPress={sendOtp}
                                        disabled={!canSendOtp}
                                        style={{
                                            width: "100%",
                                            opacity: canSendOtp ? 1 : 0.6,
                                        }}
                                    />
                                </View>
                            </View>
                        ) : null}

                        {step === "otp" ? (
                            <View style={{ gap: 14 }}>
                                <Text style={styles.h2}>Enter OTP</Text>
                                <Text style={styles.p}>Enter the 4-digit OTP to continue.</Text>

                                {otpChannels.mobile ? (
                                    <View style={styles.fullWidth}>
                                        <Input
                                            label={mobileOtpLabel}
                                            placeholder={OTP_PLACEHOLDER}
                                            keyboardType="number-pad"
                                            maxLength={OTP_MAX_LENGTH}
                                            value={mobileOtp}
                                            onChangeText={(t) => setMobileOtp(t.replace(/\D/g, ""))}
                                            textAlign="center"
                                            style={styles.otpInputText}
                                        />
                                    </View>
                                ) : null}

                                {otpChannels.email ? (
                                    <View style={styles.fullWidth}>
                                        <Input
                                            label={emailOtpLabel}
                                            placeholder={OTP_PLACEHOLDER}
                                            keyboardType="number-pad"
                                            maxLength={OTP_MAX_LENGTH}
                                            value={emailOtp}
                                            onChangeText={(t) => setEmailOtp(t.replace(/\D/g, ""))}
                                            textAlign="center"
                                            style={styles.otpInputText}
                                        />
                                    </View>
                                ) : null}

                                {otpChannels.mobile && otpChannels.email ? (
                                    <Text style={styles.infoText}>
                                        Verify either one channel to continue.
                                    </Text>
                                ) : null}

                                {resendTimer > 0 ? (
                                    <Text style={styles.timerText}>Resend OTP in {resendTimeLabel}</Text>
                                ) : (
                                    <Pressable onPress={sendOtp} disabled={loading}>
                                        <Text style={[styles.link, loading ? { opacity: 0.6 } : null]}>
                                            {loading ? "Sending..." : "Resend OTP"}
                                        </Text>
                                    </Pressable>
                                )}

                                <View style={styles.fullWidth}>
                                    <Button
                                        title={loading ? "Verifying..." : "Verify"}
                                        onPress={verifyOtp}
                                        disabled={loading || !canVerifyOtp}
                                        style={{
                                            width: "100%",
                                            opacity: loading || !canVerifyOtp ? 0.6 : 1,
                                        }}
                                    />
                                </View>

                                <Text style={styles.link} onPress={backToIdentifier}>
                                    Back
                                </Text>
                            </View>
                        ) : null}

                        {step === "reset" ? (
                            <View style={{ gap: 14 }}>
                                <Text style={styles.h2}>Set New MPIN</Text>

                                <Input
                                    label="New MPIN"
                                    placeholder="6 digits"
                                    keyboardType="number-pad"
                                    secureTextEntry
                                    maxLength={6}
                                    value={newMpin}
                                    onChangeText={(t) => setNewMpin(t.replace(/\D/g, ""))}
                                />

                                <Input
                                    label="Confirm MPIN"
                                    placeholder="Re-enter 6 digits"
                                    keyboardType="number-pad"
                                    secureTextEntry
                                    maxLength={6}
                                    value={confirmMpin}
                                    onChangeText={(t) => setConfirmMpin(t.replace(/\D/g, ""))}
                                />

                                {!isMpinValid && (newMpin.length > 0 || confirmMpin.length > 0) ? (
                                    <Text style={styles.err}>
                                        MPIN and Confirm MPIN must match (6 digits).
                                    </Text>
                                ) : null}

                                <View style={styles.fullWidth}>
                                    <Button
                                        title={loading ? "Saving..." : "Reset MPIN"}
                                        onPress={resetMpin}
                                        disabled={loading || !isMpinValid}
                                        style={{
                                            width: "100%",
                                            opacity: loading || !isMpinValid ? 0.6 : 1,
                                        }}
                                    />
                                </View>

                                <Text style={styles.link} onPress={backToOtp}>
                                    Back
                                </Text>
                            </View>
                        ) : null}
                    </View>
                </View>
            </KeyboardAvoidingView>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    center: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        paddingHorizontal: 16,
    },
    cardSmall: {
        width: "100%",
        maxWidth: 360,
        backgroundColor: "rgba(255,255,255,0.92)",
        borderRadius: 16,
        padding: 16,
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.20)",
        shadowColor: "#000",
        shadowOpacity: 0.10,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 8 },
        elevation: 4,
    },
    h2: { fontSize: 18, fontWeight: "900", color: "#4A2311", textAlign: "center" },
    p: {
        fontSize: 13,
        fontWeight: "700",
        color: "rgba(74,35,17,0.55)",
        textAlign: "center",
        marginTop: -6,
    },
    fullWidth: {
        width: "100%",
        alignSelf: "stretch",
    },
    loadingWrap: {
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 8,
    },
    otpInputText: {
        width: "100%",
        fontSize: 18,
        fontWeight: "700",
        letterSpacing: 6,
    },
    infoText: {
        marginTop: -6,
        fontSize: 12,
        fontWeight: "700",
        color: "rgba(74,35,17,0.50)",
        textAlign: "center",
    },
    err: { fontSize: 12, fontWeight: "800", color: "rgba(239,68,68,0.95)" },
    link: {
        marginTop: 4,
        fontSize: 12,
        fontWeight: "800",
        color: "rgba(234,88,12,0.95)",
        textAlign: "center",
    },
    timerText: {
        marginTop: -4,
        fontSize: 12,
        fontWeight: "800",
        color: "rgba(74,35,17,0.55)",
        textAlign: "center",
    },
});
